import { Stack, Typography } from "@mui/material";
import {
  ButtonRow,
  DateTimeField,
  Dayjs,
  Form,
  FormButton,
  FormSubmit,
  Modal,
  ModalProps,
} from "@synota-io/synota-shared-ui";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { IContractDetailsResponse } from "../../../../api/hooks/useContractDetails";
import { localDayjs, DATE_TIME_FORMAT_WITH_TZ } from "@synota-io/synota-shared-ui";
import "dayjs/plugin/timezone";
import { ReactNode } from "react";

const schema = yup.object({
  freeze_until: yup.object().required("Date and time are required to freeze this contract"),
});

type FormValues = {
  freeze_until: Dayjs;
};

const timezone = localDayjs.tz.guess();

export const FreezeContractModal = ({
  contract,
  frozenUntil,
  open,
  onClose,
  onFreeze,
  ...props
}: ModalProps & {
  contract: IContractDetailsResponse;
  frozenUntil: string | null;
  onFreeze: (freeze_until: Dayjs | null, body: ReactNode) => void;
}) => {
  const { control, handleSubmit, watch, formState } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      freeze_until: localDayjs().add(1, "day").startOf("day"),
    },
    values: frozenUntil
      ? {
          freeze_until: localDayjs(frozenUntil),
        }
      : undefined,
  });

  const values = watch();
  const freezeUntil = values.freeze_until;
  const body = freezeUntil ? (
    <Stack>
      <Typography>
        <strong>"{contract.consumer_contract_name || contract.shared_uuid}"</strong>
      </Typography>
      <Typography>{`will freeze its payments until`}</Typography>
      <Typography>
        <strong>{`${localDayjs(freezeUntil).format(DATE_TIME_FORMAT_WITH_TZ)}`}</strong>
      </Typography>
      <Typography>
        for user <strong>{contract.consumer_email}</strong>.
      </Typography>
    </Stack>
  ) : (
    "Unfreeze payments for this contract"
  );

  const onSubmit = handleSubmit((values) => {
    onFreeze(values.freeze_until, body);
  });

  return (
    <Modal
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
      title={frozenUntil ? "Freeze/Unfreeze Payments" : "Freeze Payments"}
      {...props}
      actions={
        <ButtonRow width="100%">
          <FormButton
            variant="text"
            color="error"
            onClick={() => {
              onClose();
            }}
          >
            Close
          </FormButton>

          <ButtonRow flexGrow={1}>
            {frozenUntil && (
              <FormButton
                fullWidth
                onClick={() => {
                  onFreeze(null, "Unfreeze payments for this contract");
                }}
              >
                Unfreeze
              </FormButton>
            )}
            <FormSubmit
              color="secondary"
              disabled={!formState.isValid}
              form="freeze-contract-form"
              fullWidth
              label={frozenUntil ? "Change" : "Freeze"}
            />
          </ButtonRow>
        </ButtonRow>
      }
    >
      <Form id="freeze-contract-form" onSubmit={onSubmit}>
        <DateTimeField
          autoFocus
          header={frozenUntil ? "Frozen until" : "Freeze until"}
          fullWidth
          control={control}
          clearable
          name="freeze_until"
          caption={localDayjs().add(1, "day").startOf("day").tz(timezone).format("z zzz")}
          viewRenderers={{
            hours: null,
            minutes: null,
            seconds: null,
          }}
        />
        {formState.errors.root && (
          <Typography color="error" variant="body1">
            {formState.errors.root.message}
          </Typography>
        )}
      </Form>
    </Modal>
  );
};
